import { BookmarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { ThreadEntry } from "../../../api/threads/liveMessages";
import { formatThreadEntryRef } from "../../../shared/references/threadReferences";
import { clearSelection } from "../../../shared/selection/selectedRange";
import { appPaths } from "../../../shared/routing/appPaths";
import { useThreadPreparation } from "../../threads/prepare";
import { selectedPassages, type SelectedPassages } from "./selectionPassages";
import AppButton from "../../../ui/buttons/AppButton";

/** The action's width and the gap to the selection, to keep it inside the window. */
const ACTION_WIDTH = 180;
const ACTION_GAP = 8;

/**
 * **Keep as memory** (memory.md MEM-2, ADR-0031 D5): select text in the Thread log and an
 * action appears beside the selection. It prepares "Keep a memory" beside this Thread, with
 * the selected passages quoted from their entries, each by its exact reference (an entry a
 * fork inherited names the Thread that holds it); Start there makes the memory's own small
 * Thread. Preparing writes nothing. Only a selection in `log`, this Thread's log, counts.
 */
export default function KeepAsMemory({ projectId, threadId, entries, log }: { projectId: string; threadId: string; entries: readonly ThreadEntry[]; log: RefObject<HTMLElement | null> }) {
  const { t } = useTranslation();
  const preparation = useThreadPreparation();
  const byNumber = useMemo(() => new Map(entries.map((entry) => [entry.number, entry])), [entries]);
  const [selection, setSelection] = useState<SelectedPassages | null>(null);

  useEffect(() => {
    const read = () => { setSelection(selectedPassages(log.current, byNumber)); };
    document.addEventListener("selectionchange", read);
    // The log scrolls inside the page, so the action follows the selection it belongs to.
    document.addEventListener("scroll", read, { capture: true, passive: true });
    return () => {
      document.removeEventListener("selectionchange", read);
      document.removeEventListener("scroll", read, { capture: true });
    };
  }, [byNumber, log]);

  const keep = (chosen: SelectedPassages) => {
    preparation.prepare({
      kind: "memory",
      projectId,
      returnTo: appPaths.thread(threadId),
      threadId,
      passages: chosen.passages.map((passage) => ({
        entry: formatThreadEntryRef({ threadId: passage.entry.thread_id, number: passage.entry.number }),
        quote: passage.quote,
        occurrence: passage.occurrence,
      })),
      skipped: chosen.skipped,
    });
    clearSelection();
  };

  if (selection === null) return null;
  return (
    <AppButton
      variant="primary"
      size="sm"
      className="fixed z-40 shadow-lg"
      style={{ left: Math.max(ACTION_GAP, Math.min(selection.end.x, window.innerWidth - ACTION_WIDTH - ACTION_GAP)), top: selection.end.y + ACTION_GAP }}
      // Pressing it must not clear the selection it quotes.
      onMouseDown={(event) => { event.preventDefault(); }}
      onClick={() => { keep(selection); }}
    >
      <BookmarkIcon className="h-4 w-4" aria-hidden="true" />
      {t("memories.keepAction")}
    </AppButton>
  );
}
